import type { ReactElement } from 'react';
import type { DriverView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { EMPTY } from '../shared/lib/format';
import { toneVars } from '../shared/lib/status';
import { DataTable, type Column } from '../shared/ui/DataTable';

/**
 * Список курьеров (FR-508, FR-629).
 *
 * Отдельно от общего списка пользователей: у курьера свои поля — занятость и внешний
 * код, по которому его адресует учётная система заказчика. Телефон стоит рядом с
 * именем: по нему курьер входит в приложение, и именно его спрашивают первым, когда
 * «курьер не может войти».
 *
 * Данные приходят снаружи: список перечитывает экран после заведения курьера, и два
 * независимых запроса одного и того же разошлись бы на одну строку.
 */
export function DriversTable({
  rows,
  loading,
  onCreate,
}: {
  readonly rows: readonly DriverView[];
  readonly loading: boolean;
  /** Есть право заводить курьеров — пустой список предлагает это сделать. */
  readonly onCreate?: (() => void) | undefined;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.drivers;

  const columns: readonly Column<DriverView>[] = [
    { key: 'name', title: text.fullName, render: (row) => row.full_name },
    {
      key: 'phone',
      title: text.phone,
      width: '160px',
      render: (row) => <span className="num">{row.phone ?? EMPTY}</span>,
    },
    {
      key: 'employment',
      title: text.employment,
      width: '140px',
      render: (row) =>
        row.employment === 'staff' || row.employment === 'contracted'
          ? text.employmentKinds[row.employment]
          : EMPTY,
    },
    {
      key: 'code',
      title: text.externalCode,
      width: '140px',
      render: (row) => <span className="num">{row.external_code ?? EMPTY}</span>,
    },
    {
      key: 'modes',
      title: text.departments,
      width: '220px',
      render: (row) =>
        (row.modes ?? []).length === 0 ? (
          <span style={{ color: 'var(--warn)' }}>{messages.cabinet.users.noDepartment}</span>
        ) : (
          (row.modes ?? []).map((mode) => messages.cabinet.modes[mode]).join(' · ')
        ),
    },
    {
      key: 'active',
      title: text.status,
      width: '120px',
      render: (row) => {
        const vars = toneVars(row.is_active ? 'ok' : 'neutral');

        return (
          <span style={{ ...statusStyle, color: vars.color, background: vars.background }}>
            {row.is_active ? text.statusActive : text.statusInactive}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.id}
      emptyText={
        loading
          ? messages.cabinet.common.loading
          : onCreate === undefined
            ? text.emptyReadOnly
            : text.emptyCanCreate
      }
      {...(loading || onCreate === undefined ? {} : { onReset: onCreate, resetLabel: text.add })}
    />
  );
}

const statusStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 'var(--tag-h)',
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--r-xs)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 'var(--fw-bold)',
  whiteSpace: 'nowrap',
} as const;
